import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Camera } from 'lucide-react';
import './App.css';

import LoginForm from './components/LoginForm';
import Dashboard from './components/Dashboard';
import Navigation from './components/Navigation';
import NoticeTicker from './components/NoticeTicker';

// Route-based code splitting
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const ReportsExport = lazy(() => import('./components/ReportsExport'));
const PunchSystem = lazy(() => import('./components/PunchSystem'));
const LeaveManagement = lazy(() => import('./components/LeaveManagement'));
const ProfileSetup = lazy(() => import('./components/ProfileSetup'));
const AnnouncementsAdmin = lazy(() => import('./components/AnnouncementsAdmin'));

const getApiBase = () => {
  const backend = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || '';
  if (!backend) return '/api';
  return backend.endsWith('/api') ? backend : `${backend}/api`;
};

const API = getApiBase();

// Strips large Base64 strings before saving to localStorage to prevent QuotaExceededError
const saveUserToStorage = (userData) => {
  if (!userData || typeof userData !== 'object') return;
  try {
    const {
      reference_photo,
      thumbnail_photo,
      avatar,
      photo,
      ...lightweightUser
    } = userData;
    localStorage.setItem('user', JSON.stringify(lightweightUser));
  } catch (e) {
    console.warn('Could not cache user metadata in localStorage:', e);
  }
};

// Eagerly set authorization header if token exists
const savedToken = localStorage.getItem('token');
if (savedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

const RouteLoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[50vh] w-full">
    <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Mobile Floating Punch Button (hides automatically when already on /punch)
const MobilePunchButton = ({ isAdmin }) => {
  const location = useLocation();
  if (isAdmin || location.pathname === '/punch') return null;

  return (
    <Link
      to="/punch"
      aria-label="Quick Punch"
      className="md:hidden fixed bottom-6 right-6 flex items-center justify-center w-14 h-14 bg-blue-600 hover:bg-blue-700 rounded-full shadow-[0_8px_25px_rgba(37,99,235,0.45)] text-white hover:scale-105 active:scale-95 transition-all z-40 border border-white/20"
    >
      <Camera className="w-6 h-6" />
      <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-orange-500 border-2 border-white dark:border-[#0B101E]"></span>
      </span>
    </Link>
  );
};

function App() {
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      console.error('Error parsing stored user:', e);
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(false);

  const handleLogout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const handleUserUpdate = useCallback((updatedProfile) => {
    if (!updatedProfile) return;
    setUser(prev => {
      const merged = { ...(prev || {}), ...updatedProfile };
      saveUserToStorage(merged);
      return merged;
    });
  }, []);

  // Global Axios 401 interceptor to cleanly log out expired/revoked sessions
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && !error.config?.url?.includes('/login')) {
          handleLogout();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [handleLogout]);

  // Sync profile on token change with AbortController cleanup
  useEffect(() => {
    if (!token) return;

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    const controller = new AbortController();

    const fetchUserProfile = async () => {
      try {
        const response = await axios.get(`${API}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        });
        if (response.data) {
          setUser(response.data);
          saveUserToStorage(response.data);
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Error fetching profile:', error);
          if (error.response?.status === 401) {
            handleLogout();
          }
        }
      }
    };

    fetchUserProfile();
    return () => controller.abort();
  }, [token, handleLogout]);

  const handleLogin = async (credentials) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      const { access_token, user: userData } = response.data;
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      localStorage.setItem('token', access_token);
      if (userData) {
        saveUserToStorage(userData);
      }
      setUser(userData);
      setToken(access_token);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Login failed';
      if (typeof detail === 'string') errorMsg = detail;
      else if (Array.isArray(detail)) errorMsg = detail.map(e => e.msg || JSON.stringify(e)).join(' ');
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-200">
        <LoginForm
          onLogin={handleLogin}
          loading={loading}
        />
      </div>
    );
  }

  // Wait for user object before evaluating role-protected routes
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <RouteLoadingSpinner />
      </div>
    );
  }

  const normalizedRole = String(user?.role || 'employee').trim().toLowerCase();
  const isAdmin = normalizedRole === 'admin' || normalizedRole === 'administrator';
  const isAdminOrHR = isAdmin || normalizedRole === 'hr' || normalizedRole === 'human_resources';

  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-950 transition-colors duration-200 overflow-hidden relative">
      <Router>
        <div className="shrink-0 z-50 shadow-sm relative">
          <Navigation user={user} onLogout={handleLogout} />
          <NoticeTicker user={user} />
        </div>

        <div className="flex-1 overflow-y-auto w-full relative">
          <Suspense fallback={<RouteLoadingSpinner />}>
            <Routes>
              {/* Home Route */}
              <Route
                path="/"
                element={isAdmin ? <AdminDashboard /> : <Dashboard user={user} />}
              />

              {/* Profile Route (All Authenticated Users) */}
              <Route
                path="/profile"
                element={<ProfileSetup user={user} onUserUpdate={handleUserUpdate} />}
              />

              {/* Employee & HR Only Routes (Strictly Blocked for System Admin) */}
              <Route
                path="/punch"
                element={!isAdmin ? <PunchSystem user={user} /> : <Navigate to="/" replace />}
              />
              <Route
                path="/leaves"
                element={!isAdmin ? <LeaveManagement user={user} /> : <Navigate to="/" replace />}
              />

              {/* Admin & HR Routes (Strictly Blocked for Standard Employees) */}
              <Route
                path="/admin"
                element={isAdminOrHR ? <AdminDashboard /> : <Navigate to="/" replace />}
              />
              <Route
                path="/users"
                element={isAdminOrHR ? <UserManagement user={user} /> : <Navigate to="/" replace />}
              />
              <Route
                path="/reports"
                element={isAdminOrHR ? <ReportsExport user={user} /> : <Navigate to="/" replace />}
              />

              {/* Admin-Only Route */}
              <Route
                path="/announcements"
                element={isAdmin ? <AnnouncementsAdmin /> : <Navigate to="/" replace />}
              />

              {/* Fallback Catch-All */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>

        <MobilePunchButton isAdmin={isAdmin} />
      </Router>
    </div>
  );
}

export default App;